import { uiText } from '../../i18n/uiText';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal } from 'lucide-react';

export default function ActionMenu({ actions = [], label = uiText("Другие действия"), align = 'right' }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const visibleActions = actions.filter(Boolean);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (!rootRef.current?.contains(event.target) && !panelRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    return () => {
      document.removeEventListener('pointerdown', close);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const rect = rootRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = panelRef.current?.offsetWidth || 220;
      const left = Math.max(8, Math.min(align === 'left' ? rect.left : rect.right - width, window.innerWidth - width - 8));
      const estimatedHeight = Math.min(panelRef.current?.scrollHeight || visibleActions.length * 42 + 12, window.innerHeight - 16, 360);
      const top = rect.bottom + estimatedHeight + 14 > window.innerHeight
        ? Math.max(8, rect.top - estimatedHeight - 6)
        : rect.bottom + 6;
      setPosition({ top, left });
    };
    place();
    panelRef.current?.querySelector('button:not([disabled])')?.focus({preventScroll:true});
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [align, open, visibleActions.length]);

  if (!visibleActions.length) return null;

  return (
    <div className={`row-action-menu align-${align}`} ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className="btn btn-outline btn-sm row-action-menu-toggle"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((value) => !value);
        }}
        onKeyDown={event => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault(); setOpen(true);
          }
        }}
      >
        <MoreHorizontal size={16} />
      </button>
      {open ? createPortal((
        <div
          ref={panelRef}
          className="ap-menu"
          role="menu"
          aria-label={label}
          style={{ top: position.top, left: position.left }}
          onKeyDown={event => {
            const items=Array.from(panelRef.current?.querySelectorAll('button:not([disabled])') || []);
            const index=items.indexOf(document.activeElement);
            if (event.key==='Escape') { event.preventDefault(); event.stopPropagation(); setOpen(false); triggerRef.current?.focus(); }
            else if (event.key==='Tab') { setOpen(false); triggerRef.current?.focus(); }
            else if (['ArrowDown','ArrowUp','Home','End'].includes(event.key) && items.length) {
              event.preventDefault();
              const next=event.key==='Home'?0:event.key==='End'?items.length-1:(index+(event.key==='ArrowDown'?1:-1)+items.length)%items.length;
              items[next].focus();
            }
          }}
        >
          {visibleActions.map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.key || action.label}
                type="button"
                role="menuitem"
                className={action.danger ? 'is-danger' : undefined}
                disabled={action.disabled}
                onClick={(event) => {
                  event.stopPropagation();
                  setOpen(false);
                  triggerRef.current?.focus({preventScroll:true});
                  action.onClick?.();
                }}
              >
                {Icon ? <Icon size={15} /> : null}
                {action.label}
              </button>
            );
          })}
        </div>
      ), rootRef.current?.closest('dialog[open]') || document.body) : null}
    </div>
  );
}
